<template>
  <div class="map-instance-page">
    <!-- Render CanvasEditor ONLY after elements are initialized -->
    <canvas-editor
      v-if="isElementsInitialized"
      :map-id="mapId"
      :is-map-on-focus="isMapOnFocus"
      :nodes="nodes"
      :edges="edges"
      :layers-amount="layersAmount"
      :selected-edge-id="selectedEdgeId"
      :selected-nodes="selectedNodes"
      :map-data="mapData"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { CanvasEditor, useMapArea, useElements } from '@/modules/map-editor';
import type { Emitter } from 'mitt'
import type { Node } from '@/modules/map-editor';

interface Props {
  mapId: number | string;
  isMapOnFocus?: boolean;
}

defineProps<Props>();
const store = useStore();
const emitter = inject("emitter")  as Emitter<any>;

// Vuex getters
const layersAmount = computed(() => store.getters['layers/getLayersAmount']);

const nodes = computed(() => store.getters['nodes/getNodes']);
const edges = computed(() => store.getters['edges/getEdges']);
const selectedEdgeId = computed(() => store.getters['edges/getSelectedEdgeId']);
const selectedNodes = computed(() => {
  // Access size via getSelectedCount to ensure Vue tracks Map mutations reactively
  const selectedMap = store.getters['nodes/getSelectedNodes'] as Map<number, Node> | undefined;
  const size = store.getters['nodes/getSelectedCount'] as number;
  if (!selectedMap || size === 0) return [];
  return Array.from(selectedMap.values());
});
const mapData = computed(() => store.getters['mapSettings/getMapData']);

// Elements initialization flag
const isElementsInitialized = ref(false);

// Map area service
const { mapAreaService, transferMapArea, cancelTransfer, resetCoordinates } = useMapArea();

// Elements management
const { elementsInit } = useElements(mapAreaService);

// Center map position
function centerMapPosition() {
  transferMapArea(null, {
    onStart: () => store.dispatch('maps/mapTransfer', true),
    onComplete: () => store.dispatch('maps/mapTransfer', false),
    onCancel: () => store.dispatch('maps/mapTransfer', false)
  });
}


// Lifecycle
onMounted(async () => {
  // 1. Center map position
  centerMapPosition();

  // 2. Initialize elements (populate + cache)
  await elementsInit();

  // 3. Mark as initialized to render CanvasEditor
  isElementsInitialized.value = true;

  // 4. Initialize templates
  store.dispatch('templates/objectTemplatesInit');

  // 5. Event listeners
  emitter.on('centerMapPosition', centerMapPosition);
});

onUnmounted(() => {
  cancelTransfer();
  resetCoordinates();
  store.dispatch('templates/objectTemplatesClear');
  emitter.off('centerMapPosition', centerMapPosition);
});
</script>

<style scoped lang="scss">
.map-instance-page {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>